import ConfirmationModal from "components/ConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editSummon } from "services/prosecutorDecipilineService";
import { Complaints, Investigation, Summon } from "types";
import { COMPLIANT_STATUS } from "utils/constantValues";
import { ROLES } from "utils/roles";
import AddSummonDecisionModal from "./AddSummonDecisionModal";
import AddSummonResponse from "./AddSummonResponse";
import SummonConfirmationModal from "./SummonConfirmationModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";

interface SummonProps {
  complaintData: Complaints;
  summonData: Summon;
  compliantData: Complaints;
  investigationData: Investigation;
  onSummonRefresh: any;
  onComplaintsRefresh: any;
}

export default function SummonCard({
  complaintData,
  summonData,
  compliantData,
  investigationData,
  onSummonRefresh,
  onComplaintsRefresh,
}: SummonProps) {
  const { user } = useAuth();
  const { t } = useTranslation(); // <-- Added for localization

  const canCreateSummonDecision = useHasRole([
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCoordinatorConfirmSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canAccusedConfirmSummon = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const hideViewLetterFile = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canViewLetterDoc = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canUpdateHearingDate = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewConfirmedSummon = useHasRole([ROLES.INSPECTION_PROSECUTOR]);

  const { showToast } = useToast();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isShowResponseModal, setIsShowResponseModal] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [summonExpanded, setSummonExpanded] = useState(true);
  const [documentsExpanded, setDocumentsExpanded] = useState(false);
  const [responseExpanded, setResponseExpanded] = useState(false);
  const [isEditingHearingDate, setIsEditingHearingDate] = useState(false);
  const [hearingDateDraft, setHearingDateDraft] = useState("");
  const [isUpdatingHearingDate, setIsUpdatingHearingDate] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const calculateHearingDays = () => {
    if (!summonData?.hearing_date) return null;

    const hearingDate = new Date(summonData.hearing_date);
    const today = new Date();

    // Reset time to compare only dates
    hearingDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const timeDiff = hearingDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return daysDiff;
  };

  const getHearingStatus = (days: number) => {
    if (days < 0)
      return {
        text: "Hearing date has passed",
        color: "text-red-600",
        bg: "bg-red-100",
        border: "border-red-200",
      };
    if (days === 0)
      return {
        text: "Hearing is today",
        color: "text-orange-600",
        bg: "bg-orange-100",
        border: "border-orange-200",
      };
    if (days <= 3)
      return {
        text: `${days} days remaining`,
        color: "text-orange-600",
        bg: "bg-orange-100",
        border: "border-orange-200",
      };
    if (days <= 7)
      return {
        text: `${days} days remaining`,
        color: "text-yellow-600",
        bg: "bg-yellow-100",
        border: "border-yellow-200",
      };
    return {
      text: `${days} days remaining`,
      color: "text-green-600",
      bg: "bg-green-100",
      border: "border-green-200",
    };
  };

  const handleConfirm = async () => {
    if (!summonData) return;
    setSubmitLoading(true);

    const isAccusedConfirmed = summonData?.accused_confirmed
      ? summonData?.accused_confirmed
      : canAccusedConfirmSummon;

    const isCoordinatorConfirmed = summonData?.cordinator_confirmed
      ? summonData?.cordinator_confirmed
      : false;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("cordinator_confirmed", isCoordinatorConfirmed.toString());

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setShowConfirmationModal(false);
      showToast("Summon confirmed successfully", "success");
    } catch (err: any) {
      showToast("Failed to confirm summon", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const startHearingDateEdit = () => {
    setHearingDateDraft(toDateInputValue(summonData?.hearing_date));
    setIsEditingHearingDate(true);
  };

  const cancelHearingDateEdit = () => {
    setIsEditingHearingDate(false);
    setHearingDateDraft("");
  };

  const handleUpdateHearingDate = async () => {
    if (!summonData?.id) {
      showToast("Summon not found", "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.hearing_date);
    if (!hearingDateDraft || hearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingHearingDate(true);
    try {
      await editSummon(
        {
          hearing_date: hearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelHearingDateEdit();
      showToast("Hearing date updated", "success");
    } catch (err) {
      showToast("Failed to update hearing date", "error");
    } finally {
      setIsUpdatingHearingDate(false);
    }
  };

  const accusedSummonFile = summonData?.documents?.filter(
    (a: any) => a?.document_type === "accused_response",
  ) as any;

  if (!summonData) return null;

  const hearingDays = calculateHearingDays();
  const hearingStatus =
    hearingDays !== null ? getHearingStatus(hearingDays) : null;

  return (
    <div className="mb-8">
      {(canViewSummon ||
        (canViewConfirmedSummon && summonData?.cordinator_confirmed)) &&
        summonData?.hearing_date && (
          <>
            {/* Main Summon Container */}
            <div
              className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4 ${compliantData?.status === COMPLIANT_STATUS.CLOSED || compliantData?.status === COMPLIANT_STATUS.TERMINATED ? "border-red-300" : ""}`}
            >
              {/* Summon Header with Hearing Countdown */}
              <div className="bg-white border-b border-gray-200 px-6 py-4">
                <button
                  onClick={() => setSummonExpanded(!summonExpanded)}
                  className="w-full text-left focus:outline-none"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                        <svg
                          className="w-5 h-5 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-800">
                          Hearing Summon
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">
                            Hearing Date: {summonData?.hearing_date || "-"}
                          </span>
                          {canUpdateHearingDate && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                startHearingDateEdit();
                              }}
                              className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                            >
                              {t("updateDate")}
                            </button>
                          )}
                          {hearingStatus && (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${hearingStatus.bg} ${hearingStatus.color} ${hearingStatus.border}`}
                            >
                              {hearingStatus.text}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform ${summonExpanded ? "rotate-180" : "rotate-0"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>
              </div>

              {summonExpanded && (
                <div className="p-5">
                  {/* Accused Prosecutor Info */}
                  {summonData?.accused_prosecutor_name && (
                    <div className="mb-6">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="relative">
                          <img
                            src={require("../../../images/avatar.png")}
                            alt="avatar"
                            className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
                          />
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">
                            {summonData?.accused_prosecutor_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Accused Prosecutor
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Hearing Details */}
                  <div className="mb-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hearing Date
                        </p>
                        <p className="text-sm font-medium text-gray-800">
                          {summonData?.hearing_date || "-"}
                        </p>
                      </div>
                      {hearingStatus && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </p>
                          <div
                            className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${hearingStatus.bg} ${hearingStatus.color} ${hearingStatus.border}`}
                          >
                            {hearingStatus.text}
                          </div>
                        </div>
                      )}
                    </div>

                    {isEditingHearingDate && (
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Hearing Date
                          </label>
                          <input
                            type="date"
                            value={hearingDateDraft}
                            onChange={(e) =>
                              setHearingDateDraft(e.target.value)
                            }
                            className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isUpdatingHearingDate}
                            onClick={handleUpdateHearingDate}
                            className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                          >
                            {isUpdatingHearingDate
                              ? "Updating..."
                              : "Save Date"}
                          </button>
                          <button
                            type="button"
                            disabled={isUpdatingHearingDate}
                            onClick={cancelHearingDateEdit}
                            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address Information
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-sm font-medium text-gray-800 border border-gray-200 shadow-sm p-3 rounded-lg">
                        <div>Region: {summonData?.region || "N/A"}</div>
                        <div>Zone: {summonData?.zone || "N/A"}</div>
                        <div>Woreda: {summonData?.woreda || "N/A"}</div>
                        <div>Kebele: {summonData?.kebele || "N/A"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Summon Summary */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Summon Summary
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {summonData?.summary || "No summary provided"}
                      </p>
                    </div>
                  </div>

                  {/* Documents Section */}
                  <div className="border-t border-gray-200 pt-6">
                    <button
                      onClick={() => setDocumentsExpanded(!documentsExpanded)}
                      className="w-full text-left focus:outline-none mb-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <h4 className="text-sm font-semibold text-gray-700">
                            Summon Documents
                          </h4>
                        </div>
                        <svg
                          className={`w-4 h-4 text-gray-600 transform transition-transform ${documentsExpanded ? "rotate-180" : "rotate-0"}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {documentsExpanded &&
                      summonData?.documents &&
                      summonData?.documents.length > 0 && (
                        <div className="space-y-4">
                          <DocumentViewerModal
                            documents={
                              summonData?.documents?.filter(
                                (a: any) =>
                                  a?.document_type === "letter_upload",
                              ) as any
                            }
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                            layout="list"
                            hide={hideViewLetterFile}
                            label="Official Letter"
                            hideEmptyFile={true}
                          />

                          <DocumentViewerModal
                            documents={
                              summonData?.documents?.filter(
                                (a: any) => a?.document_type === "summon_file",
                              ) as any
                            }
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                            layout="list"
                            label="Summon Document"
                            hideEmptyFile={true}
                          />

                          <DocumentViewerModal
                            documents={accusedSummonFile || []}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                            layout="list"
                            label="Accused Response"
                            hideEmptyFile={true}
                          />
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mb-4">
              {canCreateSummonDecision &&
                (accusedSummonFile || [])?.length === 0 && (
                  <button
                    className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
                    type="submit"
                    onClick={() => setIsOpen(true)}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Add Prosecutor Response
                  </button>
                )}
              {canCoordinatorConfirmSummon &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                summonData?.documents?.filter(
                  (a: any) => a?.document_type === "accused_response",
                ) &&
                !summonData?.cordinator_confirmed && (
                  <button
                    className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
                    type="submit"
                    onClick={() => setCoordinatorConfirmationModal(true)}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Confirm Summon
                  </button>
                )}
            </div>
          </>
        )}

      {/* Response and Letter Documents */}
      {(summonData?.letter_upload_details ||
        summonData?.accused_response_details) && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <button
              onClick={() => setResponseExpanded(!responseExpanded)}
              className="w-full text-left focus:outline-none"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                    <svg
                      className="w-5 h-5 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">
                      Official Documents
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Response letters and official correspondence
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${responseExpanded ? "rotate-180" : "rotate-0"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>
          </div>

          {responseExpanded && (
            <div className="p-5 space-y-6">
              {/* Accused Prosecutor Response */}
              {summonData?.accused_response_details && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Accused Prosecutor Response
                  </h4>
                  <div className="flex justify-between items-center p-3 bg-red-50 border border-red-100 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded">
                        <span className="text-xs font-medium text-red-700">
                          PDF
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {summonData?.accused_response_details?.file_name ||
                            "Response Document"}
                        </p>
                        <p className="text-xs text-gray-500">
                          Official response submission
                        </p>
                      </div>
                    </div>
                    <FileActions
                      fileUrl={summonData?.accused_response_details?.url || ""}
                    />
                  </div>
                </div>
              )}

              {/* Official Letter Document */}
              {canViewLetterDoc && summonData?.letter_upload_details && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Official Summon Letter
                  </h4>
                  <div className="flex justify-between items-center p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-blue-100 border border-blue-200 rounded">
                        <span className="text-xs font-medium text-blue-700">
                          PDF
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {summonData?.letter_upload_details?.file_name ||
                            "Official Letter"}
                        </p>
                        <p className="text-xs text-gray-500">
                          Government-issued summon letter
                        </p>
                      </div>
                    </div>
                    <FileActions
                      fileUrl={summonData?.letter_upload_details?.url || ""}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Response Button for Accused */}
      {canAccusedConfirmSummon &&
        summonData?.documents?.filter(
          (i: { document_type: string }) =>
            i?.document_type === "accused_response",
        )?.length === 0 &&
        summonData?.hearing_date &&
        !summonData?.accused_response_details && (
          <div className="flex justify-end mt-4">
            <button
              className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
              type="submit"
              onClick={() => setIsShowResponseModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              {t("submitAccusedResponse")}
            </button>
          </div>
        )}

      {/* Modals */}
      {summonData && (
        <AddSummonDecisionModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          summonData={summonData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && complaintData && investigationData && (
        <SummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={complaintData}
          summonData={summonData}
          investigationData={investigationData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {summonData && complaintData && (
        <AddSummonResponse
          isOpen={isShowResponseModal}
          setIsOpen={setIsShowResponseModal}
          complaintData={complaintData}
          summonData={summonData}
          onRefresh={onSummonRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirm}
        isLoading={submitLoading}
      />
    </div>
  );
}
